/**
 * A person's access, as the visit draws it (P15, P16): the Identity and
 * access Fact with its two axes, LMS membership and the Google Workspace
 * account, each said with its own owner (C4); then the work access needs.
 * A person who signs in has explicit LMS access control. Directory archive
 * and offboarding belong to person management. A person without access has
 * their invitation: a
 * fresh setup email on it, or an invitation to sign in when there is none.
 * Nothing here matches a person by email or name (ADR 0034).
 */
import { useWorkspaceAdoption } from "./workspace-adoption";
import { useQueryClient } from "@tanstack/react-query";

import { useTabDraft } from "@/components/ui/box-draft";
import type { Blocker, Box, BoxState, FactBox, ListBox, Part, Read, Result, TaskBox } from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { useManagedWorkspace } from "@/components/invitations/use-invitation-actions";
import { useAuth } from "@/contexts/AuthContext";
import { ACCOUNTS_QUERY_KEY, accountsApi } from "@/lib/api/accounts";
import { checkCommit } from "@/lib/api/commitSeams";
import { invitationsApi, type DeliveryState, type SingleInvitationResult } from "@/lib/api/invitations";
import { PEOPLE_QUERY_KEY, peopleApi, type PeopleCurrentInvitation, type PeopleDetailResponse } from "@/lib/api/people";

import { peopleText as text, peopleWords as words, type InviteRole } from "../people-copy";
import { formatDay, invitableStudentId, lifecycleEventLabel, membershipRoleLabel } from "../person-record";
import { imam, type PendingCommit, useTaskCommit } from "./commit";
import { PersonVisitButton } from "./person-management-actions";

const box = <T,>(spec: FactBox<T> | ListBox<T, unknown> | TaskBox<T>) => spec as unknown as Box;

/* ------------------------------------------------------------------ */
/* The two axes                                                        */
/* ------------------------------------------------------------------ */

/**
 * The LMS sign-in in one word, from the membership's own active flag. A
 * retirement on record is a suspended sign-in and restores the same way; its
 * event stays in the account's history.
 */
export function signInWord(detail: PeopleDetailResponse): string {
  const membership = detail.identity_and_access?.membership;
  if (!membership) return words.noSignIn;
  return membership.is_active ? words.signInActive : words.signInSuspended;
}

const lmsAxis = (detail: PeopleDetailResponse) => `${signInWord(detail)}.`;

export function workspaceAxis(detail: PeopleDetailResponse): string | null {
  if (!detail.identity_and_access?.membership) return null;
  if (detail.observed_existing_workspace_admin) return "Workspace is an existing administrator account IMAM doesn't manage.";
  const managed = detail.managed_state;
  if (!managed) return detail.portal_identity_posture === "legacy_unmanaged" ? "Workspace isn't managed by the institution." : null;
  if (managed.desired === "suspended" && managed.observed !== "suspended") return "Workspace suspension isn't confirmed yet.";
  if (managed.incomplete) return "Workspace is being restored.";
  switch (managed.observed) {
    case "active":
      return "Workspace is active.";
    case "suspended":
      return "Workspace is suspended.";
    case "missing":
      return "Workspace account wasn't found.";
    case "archived":
      return "Workspace account is archived.";
    default:
      return "Workspace hasn't been read back.";
  }
}

/** Settled administration stays quiet; unfinished provider work remains discoverable. */
export function accessSummary(detail: PeopleDetailResponse): string {
  const managed = detail.managed_state;
  const unsettled = managed && (managed.incomplete || managed.recovery_required || managed.observed === "unknown" || managed.observed === "missing" || managed.desired !== managed.observed) ? workspaceAxis(detail) : null;
  return [`${signInWord(detail)}.`, detail.current_invitation ? `${invitationWord(detail.current_invitation).word}.` : !detail.identity_and_access?.membership ? "No invitation issued." : null, unsettled].filter(Boolean).join(" ");
}

function workspaceChecked(detail: PeopleDetailResponse): string {
  const at = detail.managed_state?.last_verified_at ?? detail.observed_existing_workspace_admin?.last_verified_at ?? null;
  if (!detail.managed_state && !detail.observed_existing_workspace_admin) return "";
  return at ? text.workspaceChecked(formatDay(at)) : words.workspaceNeverRead;
}

/* ------------------------------------------------------------------ */
/* The invitation's own word                                           */
/* ------------------------------------------------------------------ */

/** The invitation's state in one word: the one a register row shows and the resend rests on (CASE-030). */
export function invitationWord(invitation: PeopleCurrentInvitation): BoxState {
  if (invitation.state === "accepted") return { class: "done", word: "Invitation used" };
  if (invitation.state === "revoked") return { class: "waiting", word: "Invitation revoked" };
  if (invitation.state === "failed") return { class: "waiting", word: "Account setup failed" };
  if (invitation.is_expired) return { class: "waiting", word: "Invitation expired" };
  const delivery = invitation.delivery_state as DeliveryState;
  if (delivery === "failed" || invitation.last_delivery_error) return { class: "waiting", word: "Setup email not delivered" };
  if (delivery === "ambiguous") return { class: "reading", word: "Delivery not confirmed" };
  if (invitation.state === "provisioning") return { class: "reading", word: "Provisioning" };
  if (invitation.state === "claiming") return { class: "quiet", word: "Activating" };
  if (delivery === "sent") return { class: "quiet", word: "Invitation sent" };
  return { class: "waiting", word: "Setup email not sent" };
}

/** Whether a fresh setup email can go on this invitation: anything but one already used. */
export function resendable(invitation: PeopleCurrentInvitation | null): invitation is PeopleCurrentInvitation {
  return invitation !== null && invitation.state !== "accepted";
}

/* ------------------------------------------------------------------ */
/* The boxes                                                           */
/* ------------------------------------------------------------------ */

export interface AccessBoxes {
  fact: Box;
  /** The Tasks access needs now, in the order the work happens. */
  work: Box[];
  /** The invitation and the account's history: read on the access job. */
  records: Box[];
  /** The Task the access job opens, if any. */
  opens: string | null;
}

interface InviteDraft {
  given: string;
  family: string;
  account: string;
  delivery: string;
  role: "student" | "instructor" | "admin";
}

function splitName(fullName: string | null): { given: string; family: string } {
  const parts = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return { given: parts[0] ?? "", family: "" };
  return { given: parts.slice(0, -1).join(" "), family: parts[parts.length - 1] };
}

export function useAccessBoxes(personId: string | null, detailRead: Read<PeopleDetailResponse>): AccessBoxes | null {
  const adoption = useWorkspaceAdoption(personId, detailRead);
  const client = useQueryClient();
  const { me } = useAuth();
  const { managedDomain } = useManagedWorkspace();
  const detail = detailRead.status === "ready" ? detailRead.value : detailRead.status === "stale" ? detailRead.last : null;
  const membership = detail?.identity_and_access?.membership ?? null;
  const userId = detail?.identity_and_access?.user_id ?? null;
  const invitation = detail?.current_invitation ?? null;
  const name = detail?.person.full_name ?? "";
  const pid = personId ?? "none";

  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: PEOPLE_QUERY_KEY }),
    client.invalidateQueries({ queryKey: ACCOUNTS_QUERY_KEY }),
    client.invalidateQueries({ queryKey: ["invitations"] }),
    client.invalidateQueries({ queryKey: ["dashboard", "admin"] }),
  ]);

  const nameParts = splitName(detail?.person.full_name ?? null);
  const invite = useTabDraft<InviteDraft, PendingCommit>({
    box: "person-invite",
    record: `person:${pid}`,
    version: null,
    initial: { given: nameParts.given, family: nameParts.family, account: "", delivery: detail?.person.email ?? "", role: detail && invitableStudentId(detail) ? "student" : "instructor" },
  });

  const withdraw = useTaskCommit({
    subject: `person:${pid}`,
    generation: String(membership?.is_active),
    words: { refused: words.withdrawRefused, unknown: words.withdrawUnknown, unknownSentence: words.withdrawUnknownSentence, checkNote: words.withdrawCheckNote },
    refresh,
  });
  const resend = useTaskCommit({
    subject: `person:${pid}`,
    words: { refused: words.resendRefused, unknown: words.resendUnknown, unknownSentence: words.resendUnknownSentence, checkNote: words.resendCheckNote },
    refresh,
  });
  const inviting = useTaskCommit({
    subject: `person:${pid}`,
    words: { refused: words.inviteRefused, unknown: words.inviteUnknown, unknownSentence: words.inviteUnknownSentence, checkNote: words.inviteCheckNote },
    refresh,
    keep: invite,
    // Check reads this key's receipt: only the invitation this send made. A key kept before the
    // route kept receipts has none, which proves nothing, so that one reads the person as before.
    checkBy: async (key, receipted) => {
      if (!receipted) return personId && (await peopleApi.detail(personId)).current_invitation ? "confirmed" : "unknown";
      const checked = await checkCommit(key);
      if (!("receipt" in checked)) return checked.state === "not_committed" ? "not_committed" : "unknown";
      return issued(checked.receipt.result as unknown as SingleInvitationResult, key);
    },
  });
  // The role the invitation carries: the kept draft's, so a Check on its receipt names the same one.
  const inviteRole: InviteRole = invite.value.role ?? (detail && invitableStudentId(detail) ? "student" : "instructor");
  /** One answer for Invite to sign in, from the send or from its receipt (CASE-014). */
  function issued(answer: SingleInvitationResult, key: string): Result {
    const effects = [text.createdAccount(name, inviteRole), words.setupEmailSent];
    if (answer.state === "confirmed") return inviting.confirmed(effects, [words.loginJoinsRecord]);
    if (answer.state === "unknown") return inviting.unknown(key, effects, [words.loginJoinsRecord]);
    // Refused before anything was made keeps the draft; made, with its email not sent, says both.
    if (!answer.invitation_id) return inviting.refused(answer.reason?.message ?? words.thisDidNotGoThrough);
    return inviting.confirmed([text.createdAccount(name, inviteRole)], [answer.reason?.message ?? words.inviteRefused]);
  }
  // An Invite whose answer is not known keeps its Check, even once the invitation it may have made is read.
  const inviteOpen = inviting.result.state === "unknown";

  if (!personId) return null;
  const at = (id: string) => `${id}-${personId}`;

  /* -------- Identity and access -------- */
  const work: Box[] = [];
  const records: Box[] = [];
  let opens: string | null = adoption.box?.id ?? null;
  if (adoption.box) work.push(adoption.box);
  const noAccount = detail !== null && !membership;
  const archived = Boolean(detail?.person.archived_at);
  const invitable = !adoption.offered && detail && !membership && !invitation;
  // An archived person's sign-in work stays in place, blocked, with Restore to People as its repair (F5).
  const restorePart = (from: string): Part => ({
    id: at(`${from}-restore`),
    label: words.archivedPart,
    state: { class: "waiting", word: words.archivedWord },
    owner: imam,
    fold: "never",
    draft: "none",
    render: () => (
      <div className="grid gap-2 text-ui">
        <p>{words.archivedInviteSummary}</p>
        <div><PersonVisitButton personId={pid} job="archive" from={at(from)} label={words.restoreToPeople} /></div>
      </div>
    ),
  });
  const restoreFirst = (from: string): Blocker[] => archived ? [{ kind: "missing", part: at(`${from}-restore`), sentence: words.restoreFirst }] : [];
  const fact = box<PeopleDetailResponse>({
    id: at("access"),
    kind: "fact",
    job: words.accessJob,
    label: words.accessLabel,
    owner: imam,
    fold: "never",
    read: detailRead.status === "failed"
      ? { ...detailRead, sentence: words.accessUnavailable, detail: words.accessUnavailableDetail }
      : detailRead,
    prose: (value) => text.accessProse(lmsAxis(value), workspaceAxis(value)),
    detail: (value) =>
      value.identity_and_access?.membership
        ? text.role(membershipRoleLabel(value.identity_and_access.membership))
        : adoption.offered
          ? "Existing Google account access is reviewed below."
        : value.current_invitation
          ? words.invitationBelow
          : invitable
            ? archived ? words.archivedNoInvitation : words.inviteBelow
            : words.noInvitationWork,
    sub: (value) => workspaceChecked(value),
  });

  /* -------- A person who signs in -------- */
  if (detail && membership && userId) {
    const active = membership.is_active;
    const blocked: Blocker[] =
      me?.user_id === userId
        ? [{ kind: "missing", part: at("withdraw-access"), sentence: words.selfBlocked }]
        : membership.is_super_admin
          ? [{ kind: "missing", part: at("withdraw-access"), sentence: words.superAdminBlocked }]
          : membership.role === "admin" && !me?.is_super_admin
            ? [{ kind: "missing", part: at("withdraw-access"), sentence: words.adminAuthorityBlocked }]
            : [];
    const effects = active
      ? [
          words.withdrawBlocked,
          "Google Workspace and academic relationships stay as they are",
          words.sessionNotGuaranteed,
          words.recordsKept,
        ]
      : [words.accessReturns, "Google Workspace and academic relationships stay as they are", words.recordsKept];
    work.push(box<PeopleDetailResponse>({
      id: at("withdraw"),
      kind: "task",
      job: words.withdrawJob,
      label: active ? words.withdrawLabel : words.restoreAccessLabel,
      owner: imam,
      fold: "choice",
      read: detailRead,
      detail: () => (active ? words.withdrawDetail : words.withdrawnDetail),
      state: () => resultState(withdraw.result, active ? { class: "quiet", word: words.signInActive } : { class: "quiet", word: words.signInSuspended }),
      act: { does: "open", verb: active ? words.withdrawLabel : words.restoreAccessLabel },
      candidate: { eligible: true, blocked: null, rank: null },
      parts: [{
        id: at("withdraw-access"),
        label: words.withdrawPart,
        state: active ? { class: "quiet", word: words.signInActive } : { class: "quiet", word: words.signInSuspended },
        markedByBox: true,
        owner: imam,
        fold: "never",
        draft: "none",
        summary: words.withdrawPartSummary,
      }],
      commit: {
        verb: active ? words.withdrawLabel : words.restoreAccessLabel,
        cap: active ? words.withdrawCap : words.restoreAccessCap,
        effects: () => effects.map((sentence, index) => ({ id: `effect-${index}`, sentence })),
        blockers: () => blocked,
        run: (key) => withdraw.run(key, { effects, write: async (commitKey) => void (await accountsApi.setActive(userId, !active, commitKey)) }),
      },
      result: withdraw.result,
      draft: false,
    }));
  }

  if (detail && membership && userId) {
    records.push(box<PeopleDetailResponse>({
      id: at("history"),
      kind: "list",
      job: words.historyJob,
      label: words.historyLabel,
      owner: imam,
      fold: "choice",
      act: { does: "open", verb: words.open },
      read: detail.account_lifecycle_history?.length ? detailRead : { status: "empty", sentence: words.historyEmpty },
      detail: () => "",
      state: (value) => ({ class: "quiet", word: text.plural(value.account_lifecycle_history?.length ?? 0, "event", "events") }),
      rows: (value) => value.account_lifecycle_history ?? [],
      row: {
        key: (event) => event.id,
        title: (event) => lifecycleEventLabel(event.event),
        facts: (event) => [formatDay(event.occurred_at), event.note].filter(Boolean).join(" · "),
      },
    } as ListBox<PeopleDetailResponse, NonNullable<PeopleDetailResponse["account_lifecycle_history"]>[number]>));
  }

  /* -------- No account: the invitation, or an invitation to sign in -------- */
  if (!adoption.offered && noAccount && resendable(invitation) && !inviteOpen) {
    const resting = invitationWord(invitation);
    const effects = [text.resendEffect(name)];
    const sendOutcome = (outcome: Awaited<ReturnType<typeof invitationsApi.sendSetupEmail>>, key: string): Result => {
      if (outcome.state === "confirmed")
        return resend.confirmed([text.resentLine(name)], [words.invitationNowSent]);
      if (outcome.state === "refused")
        return { state: "refused", cap: words.resendRefused, sentence: outcome.reason?.message ?? words.thisDidNotGoThrough, draft: "kept" };
      return resend.unknown(key, [text.resentLine(name)], [words.invitationNowSent]);
    };
    work.push(box<PeopleDetailResponse>({
      id: at("resend"),
      kind: "task",
      job: words.resendJob,
      label: words.resendLabel,
      owner: imam,
      fold: "choice",
      read: detailRead,
      detail: () => (archived ? words.restoreFirst : words.resendDetail),
      state: () => resultState(resend.result, resting, { class: "done", word: words.sent }),
      openState: () => resultState(resend.result, { class: "draft", word: words.readyToSend }, { class: "done", word: words.sent }),
      act: { does: "open", verb: words.resendLabel },
      candidate: { eligible: !archived && resend.result.state !== "confirmed", blocked: null, rank: 1 },
      parts: [{
        id: at("resend-invitation"),
        label: words.invitationPart,
        state: resting,
        owner: imam,
        fold: "never",
        draft: "none",
        summary: words.invitationPartSummary,
      }, ...(archived ? [restorePart("resend")] : [])],
      commit: {
        verb: words.resendVerb,
        cap: words.resendCap,
        effects: () => effects.map((sentence, index) => ({ id: `effect-${index}`, sentence })),
        blockers: () => restoreFirst("resend"),
        note: words.nobodyMatched,
        run: (key) =>
          resend.run(key, {
            effects: [text.resentLine(name)],
            remains: [words.invitationNowSent],
            write: async (commitKey) => sendOutcome(await invitationsApi.sendSetupEmail(invitation.id, commitKey), commitKey),
          }),
      },
      result: resend.result,
      draft: false,
    }));
    opens = at("resend");
  } else if (!adoption.offered && detail && noAccount && (invitable || inviteOpen)) {
    const draft = invite.value;
    const studentId = invitableStudentId(detail);
    const part = at("invite-account");
    const ready = Boolean(draft.account.trim() && draft.delivery.trim() && draft.given.trim());
    const blockers = (): Blocker[] => archived ? restoreFirst("invite") : [
      ...(!managedDomain ? [{ kind: "missing" as const, part, sentence: words.noManagedDomain }] : []),
      ...(!ready ? [{ kind: "missing" as const, part, sentence: words.enterAccountAndDelivery }] : []),
    ];
    const role: InviteRole = draft.role ?? (studentId ? "student" : "instructor");
    const effects = [text.createAccount(name, role), text.roleEffect(role), text.sendSetupEmail];
    const field = (key: keyof InviteDraft, label: string, props: { type?: string; suffix?: string } = {}) => (
      <div className="grid min-w-0 gap-1.5">
        <Label htmlFor={`${part}-${key}`}>{label}</Label>
        <div className="flex min-w-0 items-center gap-2">
          <Input
            id={`${part}-${key}`}
            type={props.type ?? "text"}
            autoComplete="off"
            spellCheck={false}
            value={draft[key]}
            onChange={(event) => {
              inviting.reset();
              invite.set((was) => ({ ...was, [key]: event.target.value }));
            }}
          />
          {props.suffix && <span className="shrink-0 text-ui text-muted-foreground">@{props.suffix}</span>}
        </div>
      </div>
    );
    work.push(box<PeopleDetailResponse>({
      id: at("invite"),
      kind: "task",
      job: words.inviteJob,
      label: words.inviteLabel,
      owner: imam,
      fold: "choice",
      read: detailRead,
      detail: () => (archived ? words.restoreFirst : words.inviteDetail),
      openDetail: () => (archived ? undefined : words.inviteOpenDetail),
      state: () =>
        resultState(
          inviting.result,
          archived ? { class: "quiet", word: words.restoreFirstState } : invite.kept ? { class: "draft", word: ready ? words.readyToSend : words.managedAccountNeeded } : { class: "quiet", word: words.noSignInYet },
          { class: "done", word: words.sent },
        ),
      act: { does: "open", verb: invite.kept ? words.continueInvitation : words.inviteLabel },
      candidate: { eligible: !archived && inviting.result.state !== "confirmed", blocked: null, rank: 4 },
      parts: archived ? [restorePart("invite")] : [{
        id: part,
        label: words.accountPart,
        state: ready ? { class: "done", word: words.written } : { class: "waiting", word: words.needed },
        owner: imam,
        fold: "never",
        draft: "browser",
        keep: invite,
        render: () => (
          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5 sm:col-span-2">
              <Label htmlFor={`${part}-role`}>LMS role</Label>
              <NativeSelect id={`${part}-role`} value={draft.role ?? (studentId ? "student" : "instructor")} onChange={event => {
                inviting.reset();
                invite.set(value => ({ ...value, role: event.target.value as InviteDraft["role"] }));
              }}>
                {studentId && <option value="student">Student</option>}
                <option value="instructor">Instructor</option>
                {me?.is_super_admin && <option value="admin">Administrator</option>}
              </NativeSelect>
            </div>
            {field("given", words.givenName)}
            {field("family", words.familyName)}
            {field("account", words.managedAccount, { suffix: managedDomain ?? undefined })}
            {field("delivery", words.deliveryEmail, { type: "email" })}
          </div>
        ),
      }],
      commit: {
        verb: words.inviteVerb,
        cap: words.inviteCap,
        effects: () => effects.map((sentence, index) => ({ id: `effect-${index}`, sentence })),
        blockers,
        note: words.nobodyMatched,
        run: (key) =>
          inviting.run(key, {
            effects: [text.createdAccount(name, role), words.setupEmailSent],
            remains: [words.loginJoinsRecord],
            write: async (commitKey) => issued(await invitationsApi.issueCommit({
              managed_email: `${draft.account.trim()}@${managedDomain}`,
              delivery_email: draft.delivery.trim(),
              given_name: draft.given.trim(),
              family_name: draft.family.trim(),
              display_name: detail.person.full_name,
              role: draft.role ?? (studentId ? "student" : "instructor"),
              confirm_admin_role: draft.role === "admin",
              ...((draft.role ?? (studentId ? "student" : "instructor")) === "student" && studentId ? { student_id: studentId } : { person_id: detail.person.id }),
            }, commitKey), commitKey),
          }),
      },
      result: inviting.result,
      draft: invite.kept,
    }));
    opens = at("invite");
  }

  if (noAccount && invitation) {
    const word = invitationWord(invitation);
    records.push(box<PeopleDetailResponse>({
      id: at("invitation"),
      kind: "list",
      job: words.invitationListJob,
      label: words.invitationListLabel,
      owner: imam,
      fold: "never",
      read: detailRead,
      detail: () => words.invitationPartSummary,
      state: () => word,
      rows: (value) => (value.current_invitation ? [value.current_invitation] : []),
      row: {
        key: (row) => row.id,
        title: () => words.setupInvitation,
        facts: (row) =>
          [
            row.last_delivery_at ? text.lastSent(formatDay(row.last_delivery_at)) : words.notSentYet,
            row.is_expired ? text.expired(formatDay(row.expires_at)) : text.expires(formatDay(row.expires_at)),
          ].join(" · "),
        act: (row) => ({ does: "visit", verb: words.openInvitation, visit: `invitation:${row.id}` }),
      },
    } as ListBox<PeopleDetailResponse, PeopleCurrentInvitation>));
  }

  return { fact, work, records, opens };
}

/** A Task's resting word under its last result: done once confirmed, and the result's own word otherwise. */
export function resultState(result: Result, settled: BoxState, done?: BoxState): BoxState {
  switch (result.state) {
    case "unknown":
      return { class: "reading", word: words.notConfirmed };
    case "refused":
      return { class: "failed", word: result.cap.replace(/\.$/, "") };
    case "partial":
      return { class: "failed", word: "Partly done" };
    case "confirmed":
      return done ?? settled;
    default:
      return settled;
  }
}
